'use client';

import {createContext, type FC, memo, type PropsWithChildren, type ReactElement, useEffect, useState} from 'react';

// NB: check https://tailwindcss.com/docs/responsive-design#customizing-breakpoints
type breakpoints = 'sm' | 'md' | '2md' | 'tablet' | 'lg' | 'xl' | '2xl';

export type breakpointsResult = Record<breakpoints, boolean> & {
  init: boolean;
};

const dimensions: Record<breakpoints, number> = {
  sm: 375,
  md: 414,
  tablet: 640,
  '2md': 820,
  lg: 1024,
  xl: 1366,
  '2xl': 1512,
};

const isBP = (str: string | breakpoints): str is breakpoints => str in dimensions;

const initialState: breakpointsResult = {
  init: false,

  sm: false,
  md: false,
  '2md': false,
  tablet: false,
  lg: false,
  xl: false,
  '2xl': false,
};

export const BreakpointsContext = createContext<breakpointsResult>(initialState);

export const BreakpointsProvider: FC<PropsWithChildren<unknown>> = memo(({children}): ReactElement | null => {
  const [result, setResult] = useState<breakpointsResult>(initialState);

  useEffect(() => {
    const handler = (key: breakpoints) => {
      return ({matches}: MediaQueryListEvent) => {
        setResult(prev => ({
          ...prev,
          [key]: matches,
        }));
      };
    };

    const initResult: Partial<breakpointsResult> = {
      init: true,
    };

    Object.entries(dimensions).forEach(([breakpoint, size]) => {
      const mediaQuery = window.matchMedia(`(min-width: ${size}px)`);
      if (isBP(breakpoint)) {
        mediaQuery.addEventListener('change', handler(breakpoint));
        Object.assign(initResult, {
          [breakpoint]: mediaQuery.matches,
        });
      }
    });

    setResult(prev => ({
      ...prev,
      ...initResult,
    }));
  }, []);

  return <BreakpointsContext.Provider value={result}>{children}</BreakpointsContext.Provider>;
});

BreakpointsProvider.displayName = 'BreakpointsProvider';
